import { useState } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { formatBRL } from '@lira/shared';
import { ChartTooltip } from './ChartTooltip';

const EASE = [0.16, 1, 0.3, 1] as const;
const VBW = 320;
const VBH = 180;
const PT = 12;
const PB = 28;
const BOTTOM = VBH - PB;
const PLOT_H = BOTTOM - PT;
/** Largura máxima da barra (poucos grupos, ex.: dashboard com 3 meses). */
const BAR_W_MAX = 22;
const PAIR_GAP_MAX = 8;

export interface BarGroup {
  label: string;
  incomeCents: number;
  expenseCents: number;
}

interface GroupedBarChartProps {
  data: BarGroup[];
  incomeColor?: string;
  expenseColor?: string;
}

/**
 * Barras agrupadas receita/despesa por mês. Crescem via scaleY (origem embaixo)
 * com stagger. viewBox 0 0 320 180. Robusto a vazio / max 0 (barras zeradas).
 */
export function GroupedBarChart({
  data,
  incomeColor = 'var(--color-income)',
  expenseColor = 'var(--color-expense)',
}: GroupedBarChartProps) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState<string | null>(null);
  const n = data.length;

  if (n === 0) {
    return <div className="grid h-40 place-items-center text-sm text-muted">Sem dados</div>;
  }

  const max = Math.max(
    0,
    ...data.map((d) => Math.max(d.incomeCents, d.expenseCents)),
  );
  const slot = VBW / n;
  // Barra/gap escalam com o slot: com muitos meses (relatório, 12+) elas
  // encolhem para caber; com poucos (dashboard, 3) ficam no tamanho máximo.
  const barW = Math.min(BAR_W_MAX, slot * 0.34);
  const pairGap = Math.min(PAIR_GAP_MAX, slot * 0.16);
  const rx = Math.min(4, barW / 2);
  const heightOf = (v: number): number => (max > 0 ? (Math.max(0, v) / max) * PLOT_H : 0);

  let barIndex = 0;
  // Capturado durante o render a partir de `active` (derivação determinística):
  // posição/rótulo/valor da barra sob o cursor, para ancorar o balão de leitura.
  let activeInfo: { x: number; y: number; label: string; value: number; color: string } | null =
    null;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${VBW} ${VBH}`} className="w-full" role="img" aria-label="Receitas x despesas">
        {/* linha de base */}
        <line x1={0} y1={BOTTOM} x2={VBW} y2={BOTTOM} stroke="var(--color-border)" strokeWidth={1} />

        {data.map((group, gi) => {
          const cx = slot * gi + slot / 2;
          const incomeX = cx - barW - pairGap / 2;
          const expenseX = cx + pairGap / 2;

          const bars = [
            { key: 'in', x: incomeX, value: group.incomeCents, color: incomeColor, label: 'Receitas' },
            { key: 'ex', x: expenseX, value: group.expenseCents, color: expenseColor, label: 'Despesas' },
          ];

          return (
            <g key={gi}>
              {bars.map((bar) => {
                const h = heightOf(bar.value);
                const delay = reduce ? 0 : barIndex * 0.08;
                barIndex += 1;
                const barKey = `${gi}-${bar.key}`;
                if (active === barKey) {
                  activeInfo = {
                    x: bar.x + barW / 2,
                    y: BOTTOM - h,
                    label: `${bar.label} · ${group.label}`,
                    value: bar.value,
                    color: bar.color,
                  };
                }
                return (
                  <m.rect
                    key={bar.key}
                    x={bar.x}
                    y={BOTTOM - h}
                    width={barW}
                    height={Math.max(0, h)}
                    rx={rx}
                    fill={bar.color}
                    tabIndex={0}
                    role="img"
                    aria-label={`${bar.label} ${group.label}: ${formatBRL(bar.value)}`}
                    className="cursor-pointer outline-none [transition:opacity_.15s_ease]"
                    style={{
                      transformBox: 'fill-box',
                      transformOrigin: 'bottom',
                      opacity: active && active !== barKey ? 0.4 : 1,
                    }}
                    initial={reduce ? false : { scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: reduce ? 0 : 0.5, delay, ease: EASE }}
                    onMouseEnter={() => setActive(barKey)}
                    onMouseLeave={() => setActive((cur) => (cur === barKey ? null : cur))}
                    onFocus={() => setActive(barKey)}
                    onBlur={() => setActive((cur) => (cur === barKey ? null : cur))}
                  >
                    <title>{`${bar.label} — ${formatBRL(bar.value)}`}</title>
                  </m.rect>
                );
              })}
              <text
                x={cx}
                y={VBH - 8}
                textAnchor="middle"
                className="fill-muted"
                fontSize={10}
              >
                {group.label}
              </text>
            </g>
          );
        })}
      </svg>

      {renderTooltip(activeInfo)}
    </div>
  );
}

/** Balão de leitura da barra ativa (extraído p/ o TS estreitar o tipo fora do closure). */
function renderTooltip(
  info: { x: number; y: number; label: string; value: number; color: string } | null,
) {
  if (!info) return null;
  return (
    <ChartTooltip
      xPct={(info.x / VBW) * 100}
      yPct={(info.y / VBH) * 100}
      color={info.color}
      label={info.label}
      value={formatBRL(info.value)}
    />
  );
}
